import type { PostGetSelectedAddOnsPayload } from '@/schemas/add-ons'
import type { paymentService } from '@/services'
import type { FaqsListQueryParam } from '@/types/faqs'
import type { GetNotificationsSearchParams } from '@/types/notification'
import type {
  PaginationWithSearchQueryParam,
  PaginationWithStatusQueryParam,
} from '@/types/shared'

export const queryKeys = {
  auth: {
    login: () => ['auth/login'] as const,
    logout: () => ['auth/logout'] as const,
    register: () => ['auth/register'] as const,
    resetPassword: () => ['auth/reset-password'] as const,
    doResetPassword: () =>
      [...queryKeys.auth.resetPassword(), 'reset'] as const,
    verifyResetPassword: () =>
      [...queryKeys.auth.resetPassword(), 'verify'] as const,
  },
  user: {
    profile: () => ['user/profile'] as const,
    patch: () => ['user/profile/patch'] as const,
    verification: () => ['user/profile/delete/verification'] as const,
    delete: () => ['user/profile/delete'] as const,
    sendVerificationEmail: () =>
      ['user/profile/send-verification-email'] as const,
    verifyEmail: () => ['user/profile/verify-email'] as const,
    changePassword: () => ['user/profile/change-password'] as const,
  },
  fleet: {
    list: (params?: Pick<PaginationWithSearchQueryParam, 'search'>) =>
      ['fleet/list', params] as const,
  },
  testimonials: {
    kohbus: () => ['testimonials'] as const,
  },
  booking: {
    search: () => ['booking/search'] as const,
    create: () => ['booking/create'] as const,
    update: () => ['booking/update'] as const,
    detail: (id: string) => ['booking/detail', id] as const,
    deleteTransportation: () => ['booking/deleteTransportation'] as const,
    transportation: (id: string) => ['booking/transportation', id] as const,
    updateInfo: () => ['booking/updateInfo'] as const,
    claim: () => ['booking/claim'] as const,
    plan: () => ['booking/plan'] as const,
    applyVoucher: () => ['booking/applyVoucher'] as const,
    deleteVoucher: () => ['booking/deleteVoucher'] as const,
    checkout: () => ['booking/checkout'] as const,
    cancel: () => ['booking/cancel'] as const,
    cancelUnpaid: () => ['booking/cancelUnpaid'] as const,
    calculateCancel: () => ['booking/calculateCancel'] as const,
    updateBus: () => ['booking/updateBus'] as const,
    updateTransportationTripType: () =>
      ['booking/updateTransportationTripType'] as const,
    date: (params?: { month?: string; id: string }) =>
      ['booking/date', params] as const,
    dateTypes: (params?: { month?: string; id: string }) =>
      ['booking/date/types', params] as const,
    updateTransportationSelectedDate: () =>
      ['booking/updateTransportationSelectedDate'] as const,
    getTransportationSelectedDate: (params: { id: string; date: string }) =>
      ['booking/getTransportationSelectedTime', params] as const,
    transportationSelectedDate: () =>
      ['booking/transportationSelectedDate'] as const,
    time: (params?: { month?: string; id: string }) =>
      ['booking/time', params] as const,
    timeTypes: (params?: { time_slot?: string; id: string }) =>
      ['booking/time/types', params] as const,
    timeSlot: (params?: { time_slot?: string; id: string }) =>
      ['booking/time/slot', params] as const,
    updateTransportationSelectedTime: () =>
      ['booking/updateTransportationSelectedTime'] as const,
    getTransportationSelectedTime: (params: {
      id: string
      time: string
      depart_time?: string
      return_time?: string
    }) => ['booking/getTransportationSelectedTime', params] as const,
    transportationSelectedTime: () =>
      ['booking/transportationSelectedTime'] as const,
    updateTransportationPassenger: () =>
      ['booking/updateTransportationPassenger'] as const,
    postBookingPointsRoutes: () => ['booking/postBookingPointsRoutes'] as const,
    updateTransportationRoutesPoints: () =>
      ['booking/updateTransportationRoutesPoints'] as const,
    updateTransportationRoutesHourly: () =>
      ['booking/updateTransportationRoutesHourly'] as const,
    amend: () => ['booking/amend'] as const,
    selectPlan: () => ['booking/selectPlan'] as const,
    submitAmend: () => ['booking/submitAmend'] as const,
  },
  presign: {
    upload: () => ['presign'] as const,
    uploads: () => ['presign/uploads'] as const,
  },
  faqs: {
    list: (params?: Pick<FaqsListQueryParam, 'category_id' | 'search'>) =>
      ['faqs/list', params] as const,
    categories: () => ['faqs/categories'] as const,
  },
  step: {
    update: () => ['step/update'] as const,
  },
  myBooking: {
    list: (params?: Pick<PaginationWithStatusQueryParam, 'status'>) =>
      ['my-booking/list', params] as const,
    detail: (id: string) => [`my-booking/${id}/detail`] as const,
    delete: () => ['my-booking/delete'] as const,
    tabList: () => ['my-booking/tab-list'] as const,
  },
  payment: {
    methods: (id: string) => ['payment/methods', id] as const,
    methodSelected: (
      params: Parameters<typeof paymentService.methodSelect>[0],
    ) => ['payment/methodSelected', params] as const,
    pay: () => ['payment/pay'] as const,
    retry: () => ['payment/retry'] as const,
    uploadProof: () => ['payment/upload-proof'] as const,
    bankAccounts: () => ['payment/bank-accounts'] as const,
  },
  cancellation: {
    cancellations: () => ['cancellation/cancellations'] as const,
  },
  places: {
    list: (search: string) => ['places/list', search] as const,
    detail: (placeId: string) => ['places/list', placeId] as const,
  },
  addOns: {
    list: (
      params?: Partial<Pick<PaginationWithSearchQueryParam, 'search'>> & {
        booking_id: string
      },
    ) => ['add-ons/list', params] as const,
    selected: (id: string, params: PostGetSelectedAddOnsPayload) =>
      ['add-ons/select', id, params] as const,
    patchHourly: () => ['add-ons/patchHourly'] as const,
    patchPTP: () => ['add-ons/patchPTP'] as const,
    postSelected: () => ['add-ons/postSelected'] as const,
  },
  config: {
    additionalPoints: () => ['additional-points'] as const,
    specialLocations: () => ['special-locations'] as const,
  },
  notification: {
    tabList: () => ['notification/tab-list'] as const,
    notifications: (params: GetNotificationsSearchParams) =>
      ['notification/notifications', params] as const,
    notification: (id: string) => ['notification/notification', id] as const,
    read: () => ['notification/read'] as const,
  },
  refunded: {
    count: () => ['refunded/count'] as const,
    list: () => ['refunded/list'] as const,
    detail: (id: string) => ['refunded/detail', id] as const,
  },
  plan: {
    cancellations: () => ['plan/cancellations'] as const,
  },
  asyncs: {
    amendCalculation: () => ['asyncs/amendCalculation'] as const,
  },
} as const
